import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Euro, Loader2, Pencil, RotateCcw, Trash2, Plus, Search, Info, ChevronRight, FileDown } from "lucide-react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  getCostSettings, saveCostSettings, listCostDays, saveCostDay, resetCostDay,
  listPunctualCosts, createPunctualCost, updatePunctualCost, deletePunctualCost, listOrderCosts,
} from "@/lib/api";
import { useTableSort, SortHeader, norm } from "@/components/common/SortableTable";
import { exportPeriodsPdf, exportOrderPdf, exportOrdersPdf } from "@/lib/costsPdf";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

export const fmtEur = (v) => Number(v || 0).toLocaleString("pt-PT", { style: "currency", currency: "EUR" });
export const fmtH = (h) => {
  const tot = Math.round(Number(h || 0) * 60);
  const hh = Math.floor(tot / 60);
  const mm = tot % 60;
  if (hh === 0) return `${mm} min`;
  return `${hh}h${String(mm).padStart(2, "0")}`;
};
const fmtM = (v) => `${Number(v || 0).toLocaleString("pt-PT", { maximumFractionDigits: 1 })} m`;
const fmtDate = (d) => { if (!d) return "—"; const [y, m, dd] = d.split("-"); return `${dd}/${m}/${y}`; };
const num = (s) => { const v = parseFloat(String(s ?? "").replace(",", ".")); return Number.isNaN(v) ? null : v; };

function monthRange(offset = 0) {
  const now = new Date();
  const a = new Date(now.getFullYear(), now.getMonth() + offset, 1);
  const b = new Date(now.getFullYear(), now.getMonth() + offset + 1, 0);
  const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return { start: iso(a), end: iso(b) };
}

function Stat({ label, value, hint, testid, tone }) {
  return (
    <Card className="p-4" data-testid={testid}>
      <div className="text-xs text-muted-foreground uppercase tracking-wide">{label}</div>
      <div className={`text-2xl font-semibold mt-1 tabular-nums ${tone || ""}`} style={{ fontFamily: "Space Grotesk" }}>{value}</div>
      {hint && <div className="text-xs text-muted-foreground mt-0.5">{hint}</div>}
    </Card>
  );
}

function RangeBar({ range, setRange }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Input type="date" className="w-[160px]" value={range.start} onChange={(e) => setRange((r) => ({ ...r, start: e.target.value }))} data-testid="costs-start-date" />
      <span className="text-sm text-muted-foreground">a</span>
      <Input type="date" className="w-[160px]" value={range.end} onChange={(e) => setRange((r) => ({ ...r, end: e.target.value }))} data-testid="costs-end-date" />
      <Button variant="outline" size="sm" onClick={() => setRange(monthRange(0))} data-testid="costs-this-month">Este mês</Button>
      <Button variant="outline" size="sm" onClick={() => setRange(monthRange(-1))} data-testid="costs-last-month">Mês anterior</Button>
      <Button variant="ghost" size="sm" onClick={() => setRange({ start: "", end: "" })} data-testid="costs-all-time">Tudo</Button>
    </div>
  );
}

export default function CostsPage() {
  const [tab, setTab] = useState("orders");
  const [range, setRange] = useState(monthRange(0));
  const [version, setVersion] = useState(0);
  const [groupBy, setGroupBy] = useState(() => localStorage.getItem("costs-group-by") || "day");
  useEffect(() => { localStorage.setItem("costs-group-by", groupBy); }, [groupBy]);
  const bump = () => setVersion((v) => v + 1);

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1400px] mx-auto space-y-6">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight flex items-center gap-2" style={{ fontFamily: "Space Grotesk" }}>
          <Euro className="h-7 w-7 text-primary" /> Controlo de custos
        </h1>
        <p className="text-sm text-muted-foreground">
          O custo fixo de cada dia é dividido pelas OF conforme as horas reais de cada uma (marcadas + entre marcadas, que contam para a OF seguinte), com as regras da análise diária: pausas previstas retiradas das marcadas e do entre marcadas, horas extra contam. Desgaste e manutenção pelas horas de corte real.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="orders" data-testid="costs-tab-orders">Custo por OF</TabsTrigger>
          <TabsTrigger value="days" data-testid="costs-tab-days">Dias</TabsTrigger>
          <TabsTrigger value="punctual" data-testid="costs-tab-punctual">Custos pontuais</TabsTrigger>
          <TabsTrigger value="settings" data-testid="costs-tab-settings">Configuração</TabsTrigger>
        </TabsList>
        <TabsContent value="orders" className="mt-4"><OrdersTab range={range} setRange={setRange} version={version} groupBy={groupBy} setGroupBy={setGroupBy} /></TabsContent>
        <TabsContent value="days" className="mt-4"><DaysTab range={range} setRange={setRange} version={version} onChanged={bump} groupBy={groupBy} setGroupBy={setGroupBy} /></TabsContent>
        <TabsContent value="punctual" className="mt-4"><PunctualTab onChanged={bump} /></TabsContent>
        <TabsContent value="settings" className="mt-4"><SettingsTab onChanged={bump} /></TabsContent>
      </Tabs>
    </div>
  );
}

// ------------------------------------------------------------------ Agrupamento dia / semana / mês
const GROUP_LABELS = { day: "Dia", week: "Semana", month: "Mês" };

function isoWeek(dateStr) {
  const d = new Date(`${dateStr}T00:00:00Z`);
  const day = (d.getUTCDay() + 6) % 7; // 0 = segunda
  const monday = new Date(d); monday.setUTCDate(d.getUTCDate() - day);
  const thursday = new Date(monday); thursday.setUTCDate(monday.getUTCDate() + 3);
  const yearStart = new Date(Date.UTC(thursday.getUTCFullYear(), 0, 1));
  const week = 1 + Math.round(((thursday - yearStart) / 86400000 - 3 + ((yearStart.getUTCDay() + 6) % 7)) / 7);
  const sunday = new Date(monday); sunday.setUTCDate(monday.getUTCDate() + 6);
  const iso = (x) => x.toISOString().slice(0, 10);
  return { key: `${thursday.getUTCFullYear()}-W${String(week).padStart(2, "0")}`, week, start: iso(monday), end: iso(sunday) };
}

export function periodOf(date, groupBy) {
  if (groupBy === "week") {
    const w = isoWeek(date);
    return { key: w.key, label: `Semana ${w.week}`, sub: `${fmtDate(w.start).slice(0, 5)} – ${fmtDate(w.end)}` };
  }
  if (groupBy === "month") {
    const [y, m] = date.split("-");
    return { key: `${y}-${m}`, label: `${MONTHS_PT[Number(m) - 1]} ${y}`, sub: "" };
  }
  return { key: date, label: fmtDate(date), sub: "" };
}
const MONTHS_PT = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];

const SUM_FIELDS = ["cost_modelagem", "cost_mesa", "cost_electricity", "cost_depreciation", "cost_punctual", "fixed_cost", "work_hours", "of_hours", "own_hours", "between_hours", "idle_cost", "variable_cost", "cutting_hours", "cuts_count"];
const ORDER_SUM = ["cuts_count", "hours", "own_hours", "between_hours", "cutting_hours", "cost_fixed", "cost_variable", "cost_total"];

/** Each day keeps its own €/hora; week/month rows are the SUM of their days. */
function aggregateDays(days, groupBy) {
  const map = new Map();
  for (const d of days) {
    const p = periodOf(d.date, groupBy);
    let g = map.get(p.key);
    if (!g) {
      g = { key: p.key, label: p.label, sub: p.sub, date: d.date, days: [], overridden: false, orders: new Map() };
      SUM_FIELDS.forEach((f) => { g[f] = 0; });
      map.set(p.key, g);
    }
    g.days.push(d);
    g.overridden = g.overridden || d.overridden;
    if (d.date < g.date) g.date = d.date;
    SUM_FIELDS.forEach((f) => { g[f] += Number(d[f] || 0); });
    for (const o of d.orders || []) {
      let x = g.orders.get(o.key);
      if (!x) { x = { key: o.key, name: o.name, type: o.type, days: 0 }; ORDER_SUM.forEach((f) => { x[f] = 0; }); g.orders.set(o.key, x); }
      x.days += 1;
      ORDER_SUM.forEach((f) => { x[f] += Number(o[f] || 0); });
    }
  }
  return [...map.values()].map((g) => ({
    ...g,
    cost_per_hour: g.days.length === 1 ? g.days[0].cost_per_hour : (g.of_hours ? g.fixed_cost / g.of_hours : 0),
    orders: [...g.orders.values()].map((o) => ({ ...o, share_pct: g.of_hours ? (o.hours / g.of_hours) * 100 : 0 })).sort((a, b) => b.hours - a.hours),
  }));
}

function GroupByToggle({ value, onChange, testid }) {
  return (
    <div className="inline-flex rounded-lg border p-0.5 bg-muted/40" data-testid={testid}>
      {Object.entries(GROUP_LABELS).map(([k, l]) => (
        <button key={k} type="button" onClick={() => onChange(k)} data-testid={`${testid}-${k}`}
          className={`px-3 py-1 text-sm rounded-md ${value === k ? "bg-background shadow-sm font-medium" : "text-muted-foreground hover:text-foreground"}`}>
          {l}
        </button>
      ))}
    </div>
  );
}

const fmtPct = (v) => `${Number(v || 0).toLocaleString("pt-PT", { maximumFractionDigits: 1 })}%`;

/** How a day (or week/month) cost is split among the OFs. */
function DayBreakdown({ g, groupBy, rates, range }) {
  const varRate = (rates?.wear_per_hour || 0) + (rates?.maintenance_per_hour || 0);
  const single = groupBy === "day";
  return (
    <div className="bg-muted/30 px-4 py-3 space-y-3" data-testid={`costs-breakdown-${g.key}`}>
      <div className="flex justify-end -mb-1">
        <Button variant="outline" size="sm" className="h-7 text-xs" data-testid={`costs-breakdown-pdf-${g.key}`}
          onClick={() => exportPeriodsPdf({ groups: [g], groupBy, range, rates, filename: `custos-${g.key}.pdf` })}>
          <FileDown className="h-3.5 w-3.5 mr-1" /> PDF {single ? "deste dia" : groupBy === "week" ? "desta semana" : "deste mês"}
        </Button>
      </div>
      <div className="text-xs text-muted-foreground space-y-0.5">
        {single ? (
          <>
            <div><span className="font-medium text-foreground">Custo fixo do dia {fmtEur(g.fixed_cost)}</span> ÷ {fmtH(g.of_hours)} reais de OF ({fmtH(g.own_hours)} de marcadas + {fmtH(g.between_hours)} entre marcadas) = <span className="font-medium text-foreground">{fmtEur(g.cost_per_hour)}/hora</span>.</div>
            <div>Cada OF paga {fmtEur(g.cost_per_hour)} × as suas horas, por isso o custo do dia fica todo distribuído. O tempo entre marcadas conta para a OF seguinte (no 1.º corte, desde as 08:00). Regras da análise diária: as pausas previstas (10:00–10:10, 12:00–13:30, 16:00–16:10) são retiradas das marcadas (só da parte parada) e do entre marcadas; o trabalho antes das 08:00 e depois das 17:30 conta, exceto a espera &gt; 60 min antes de um turno extra. Ao sábado não há pausas previstas.</div>
          </>
        ) : (
          <div><span className="font-medium text-foreground">{g.days.length} dia(s) com produção.</span> Cada dia é calculado com o seu próprio €/hora (custo fixo do dia ÷ horas reais de OF desse dia) e os valores abaixo são a soma desses dias. €/hora médio: {fmtEur(g.cost_per_hour)}.</div>
        )}
        <div>Desgaste + manutenção: {fmtEur(varRate)} por hora de corte real de cada OF.</div>
      </div>
      <table className="w-full text-xs bg-background rounded-md border" data-testid={`costs-breakdown-table-${g.key}`}>
        <thead className="text-muted-foreground bg-muted/50">
          <tr>
            <th className="text-left px-3 py-2 font-medium">OF</th>
            {!single && <th className="text-right px-2 py-2 font-medium">Dias</th>}
            <th className="text-right px-2 py-2 font-medium">Cortes</th>
            <th className="text-right px-2 py-2 font-medium">Horas OF</th>
            <th className="text-right px-2 py-2 font-medium">% do tempo</th>
            <th className="text-right px-2 py-2 font-medium">Parte do custo fixo</th>
            <th className="text-right px-2 py-2 font-medium">Desgaste + manut.</th>
            <th className="text-right px-3 py-2 font-medium">Custo OF</th>
          </tr>
        </thead>
        <tbody>
          {g.orders.map((o) => (
            <tr key={o.key} className="border-t" data-testid={`costs-breakdown-row-${g.key}-${o.key}`}>
              <td className="px-3 py-1.5 font-medium">{o.name}</td>
              {!single && <td className="px-2 py-1.5 text-right tabular-nums">{o.days}</td>}
              <td className="px-2 py-1.5 text-right tabular-nums">{o.cuts_count}</td>
              <td className="px-2 py-1.5 text-right tabular-nums whitespace-nowrap">
                {fmtH(o.hours)}
                <div className="text-[10px] text-muted-foreground">{fmtH(o.own_hours)} + {fmtH(o.between_hours)} entre</div>
              </td>
              <td className="px-2 py-1.5 text-right tabular-nums">{fmtPct(o.share_pct)}</td>
              <td className="px-2 py-1.5 text-right tabular-nums whitespace-nowrap">
                {fmtEur(o.cost_fixed)}
                {single && <div className="text-[10px] text-muted-foreground">{fmtEur(g.cost_per_hour)} × {fmtH(o.hours)}</div>}
              </td>
              <td className="px-2 py-1.5 text-right tabular-nums whitespace-nowrap">
                {fmtEur(o.cost_variable)}
                <div className="text-[10px] text-muted-foreground">{fmtH(o.cutting_hours)} de corte</div>
              </td>
              <td className="px-3 py-1.5 text-right tabular-nums font-semibold">{fmtEur(o.cost_total)}</td>
            </tr>
          ))}
          <tr className="border-t bg-muted/40 font-semibold" data-testid={`costs-breakdown-total-${g.key}`}>
            <td className="px-3 py-1.5">Total</td>
            {!single && <td />}
            <td className="px-2 py-1.5 text-right tabular-nums">{g.cuts_count}</td>
            <td className="px-2 py-1.5 text-right tabular-nums">{fmtH(g.of_hours)}</td>
            <td className="px-2 py-1.5 text-right tabular-nums">100%</td>
            <td className="px-2 py-1.5 text-right tabular-nums">{fmtEur(g.fixed_cost)}</td>
            <td className="px-2 py-1.5 text-right tabular-nums">{fmtEur(g.variable_cost)}</td>
            <td className="px-3 py-1.5 text-right tabular-nums">{fmtEur(g.fixed_cost + g.variable_cost)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

/** Periods (day / week / month) of one OF, from its by_day list. */
function orderPeriods(o, groupBy) {
  const map = new Map();
  for (const d of o.by_day || []) {
    const p = periodOf(d.date, groupBy);
    let g = map.get(p.key);
    if (!g) { g = { key: p.key, label: p.label, sub: p.sub, date: d.date, days: 0, work_hours: 0 }; ORDER_SUM.forEach((f) => { g[f] = 0; }); map.set(p.key, g); }
    g.days += 1;
    g.work_hours += d.work_hours;
    g.cph = d.cost_per_hour;
    ORDER_SUM.forEach((f) => { g[f] += Number(d[f] || 0); });
  }
  return [...map.values()].sort((a, b) => a.date.localeCompare(b.date));
}

/** Per-OF list of days / weeks / months. */
function OrderBreakdown({ o, groupBy, range }) {
  const rows = useMemo(() => orderPeriods(o, groupBy), [o, groupBy]);
  const single = groupBy === "day";
  return (
    <div className="bg-muted/30 px-4 py-3 space-y-2" data-testid={`costs-order-breakdown-${o.key}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="text-xs text-muted-foreground">
          Custo de {o.name} {single ? "em cada dia" : `por ${GROUP_LABELS[groupBy].toLowerCase()}`}: €/hora do dia × horas da OF nesse dia + desgaste e manutenção pelas horas de corte real.
          {!single && " Cada dia é calculado com o seu €/hora e depois somado."}
        </div>
        <Button variant="outline" size="sm" className="h-7 text-xs" data-testid={`costs-order-pdf-${o.key}`}
          onClick={() => exportOrderPdf({ order: o, periods: rows, groupBy, range })}>
          <FileDown className="h-3.5 w-3.5 mr-1" /> PDF desta OF
        </Button>
      </div>
      <table className="w-full text-xs bg-background rounded-md border" data-testid={`costs-order-breakdown-table-${o.key}`}>
        <thead className="text-muted-foreground bg-muted/50">
          <tr>
            <th className="text-left px-3 py-2 font-medium">{GROUP_LABELS[groupBy]}</th>
            {!single && <th className="text-right px-2 py-2 font-medium">Dias</th>}
            <th className="text-right px-2 py-2 font-medium">Cortes</th>
            <th className="text-right px-2 py-2 font-medium">Horas OF</th>
            {single && <th className="text-right px-2 py-2 font-medium">€/hora do dia</th>}
            <th className="text-right px-2 py-2 font-medium">% do tempo</th>
            <th className="text-right px-2 py-2 font-medium">Parte do custo fixo</th>
            <th className="text-right px-2 py-2 font-medium">Desgaste + manut.</th>
            <th className="text-right px-3 py-2 font-medium">Custo OF</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} className="border-t" data-testid={`costs-order-breakdown-row-${o.key}-${r.key}`}>
              <td className="px-3 py-1.5 font-medium whitespace-nowrap">{r.label}{r.sub && <div className="text-[10px] text-muted-foreground font-normal">{r.sub}</div>}</td>
              {!single && <td className="px-2 py-1.5 text-right tabular-nums">{r.days}</td>}
              <td className="px-2 py-1.5 text-right tabular-nums">{r.cuts_count}</td>
              <td className="px-2 py-1.5 text-right tabular-nums whitespace-nowrap">{fmtH(r.hours)}<div className="text-[10px] text-muted-foreground">{fmtH(r.own_hours)} + {fmtH(r.between_hours)} entre</div></td>
              {single && <td className="px-2 py-1.5 text-right tabular-nums">{fmtEur(r.cph)}</td>}
              <td className="px-2 py-1.5 text-right tabular-nums">{fmtPct(r.work_hours ? (r.hours / r.work_hours) * 100 : 0)}</td>
              <td className="px-2 py-1.5 text-right tabular-nums">{fmtEur(r.cost_fixed)}</td>
              <td className="px-2 py-1.5 text-right tabular-nums">{fmtEur(r.cost_variable)}</td>
              <td className="px-3 py-1.5 text-right tabular-nums font-semibold">{fmtEur(r.cost_total)}</td>
            </tr>
          ))}
          <tr className="border-t bg-muted/40 font-semibold">
            <td className="px-3 py-1.5">Total</td>
            {!single && <td className="px-2 py-1.5 text-right tabular-nums">{rows.reduce((a, r) => a + r.days, 0)}</td>}
            <td className="px-2 py-1.5 text-right tabular-nums">{o.cuts_count}</td>
            <td className="px-2 py-1.5 text-right tabular-nums">{fmtH(o.hours)}</td>
            {single && <td />}
            <td />
            <td className="px-2 py-1.5 text-right tabular-nums">{fmtEur(o.cost_fixed)}</td>
            <td className="px-2 py-1.5 text-right tabular-nums">{fmtEur(o.cost_wear + o.cost_maintenance)}</td>
            <td className="px-3 py-1.5 text-right tabular-nums">{fmtEur(o.cost_total)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

// ------------------------------------------------------------------ Custo por OF
const TYPE_LABEL = { of: "Com OF", no_of: "Sem OF", all: "Todas" };
const ORDER_GETTERS = {
  name: (o) => o.name,
  hours: (o) => o.hours,
  modelagem: (o) => o.cost_modelagem,
  mesa: (o) => o.cost_mesa,
  electricity: (o) => o.cost_electricity,
  depreciation: (o) => o.cost_depreciation,
  punctual: (o) => o.cost_punctual,
  wear: (o) => o.cost_wear,
  maintenance: (o) => o.cost_maintenance,
  total: (o) => o.cost_total,
};
const DAY_GETTERS = {
  date: (d) => d.date,
  modelagem: (d) => d.cost_modelagem,
  mesa: (d) => d.cost_mesa,
  electricity: (d) => d.cost_electricity,
  punctual: (d) => d.cost_punctual,
  fixed: (d) => d.fixed_cost,
  per_hour: (d) => d.cost_per_hour,
  of_hours: (d) => d.of_hours,
  depreciation: (d) => d.cost_depreciation,
  cuts: (d) => d.cuts_count,
};
const PUNCTUAL_GETTERS = {
  description: (p) => p.description,
  period: (p) => p.period,
  amount: (p) => p.amount,
  per_day: (p) => p.per_day,
};
function OrdersTab({ range, setRange, version, groupBy, setGroupBy }) {
  const [open, setOpen] = useState({});
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState("of");
  const [q, setQ] = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const p = { type };
    if (range.start) p.start = range.start;
    if (range.end) p.end = range.end;
    listOrderCosts(p)
      .then((r) => { if (alive) setData(r); })
      .catch(() => { if (alive) toast.error("Falha ao carregar custos"); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [range.start, range.end, type, version]);

  const items = useMemo(() => {
    const l = data?.orders || [];
    if (!q.trim()) return l;
    const n = norm(q.trim());
    return l.filter((o) => norm(`${o.name} ${o.key} ${o.sample_description || ""}`).includes(n));
  }, [data, q]);
  const sort = useTableSort(items, ORDER_GETTERS, "total", "desc");
  const t = data?.totals;
  const noCosts = data && data.settings && !data.settings.modelagem_rate && !data.settings.mesa_rate && !data.settings.electricity_day;

  return (
    <div className="space-y-4">
      <Card className="p-3 sm:p-4 flex flex-col lg:flex-row lg:items-center gap-3">
        <RangeBar range={range} setRange={setRange} />
        <div className="relative flex-1 min-w-[200px]">
          <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
          <Input placeholder="Procurar OF, nome ou descrição…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="costs-search" />
        </div>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="w-[140px]" data-testid="costs-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="of">Com OF</SelectItem>
            <SelectItem value="no_of">Sem OF</SelectItem>
            <SelectItem value="all">Todas</SelectItem>
          </SelectContent>
        </Select>
      </Card>

      {noCosts && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 text-amber-900 p-3 text-sm flex gap-2" data-testid="costs-no-settings">
          <Info className="h-4 w-4 mt-0.5 shrink-0" /> Ainda não definiu os valores padrão. Vá a “Configuração” para indicar pessoas, valor pessoa/dia e eletricidade.
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat testid="costs-stat-total" label="Custo total do período" value={t ? fmtEur(t.total) : "—"} hint={t ? `${t.days_count} dia(s) com produção` : ""} />
        <Stat testid="costs-stat-attributed" label="Atribuído às OF" value={t ? fmtEur(t.attributed) : "—"} hint={t ? `${fmtH(t.of_hours)} de OF` : ""} />
        <Stat testid="costs-stat-cph" label="€/hora médio" value={t && t.of_hours ? fmtEur(t.fixed_cost / t.of_hours) : "—"} hint="custo fixo ÷ horas reais de OF" />
        <Stat testid="costs-stat-rates" label="Desgaste + manutenção" value={data ? `${fmtEur((data.rates?.wear_per_hour || 0) + (data.rates?.maintenance_per_hour || 0))}/h` : "—"} hint="por hora de corte real" />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">Detalhe de cada OF por:</span>
          <GroupByToggle value={groupBy} onChange={setGroupBy} testid="costs-orders-groupby" />
        </div>
        <p className="text-xs text-muted-foreground">Clique numa OF para ver o custo {groupBy === "day" ? "em cada dia" : groupBy === "week" ? "em cada semana" : "em cada mês"}.</p>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="sm" variant="outline" disabled={!items.length} data-testid="costs-orders-pdf">
              <FileDown className="h-4 w-4 mr-1" /> Exportar PDF
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem data-testid="costs-orders-pdf-list"
              onClick={() => exportOrdersPdf({ orders: sort.sorted, groupBy, range, withDetail: false, typeLabel: TYPE_LABEL[type] })}>
              Lista de OF (resumo)
            </DropdownMenuItem>
            <DropdownMenuItem data-testid="costs-orders-pdf-detail"
              onClick={() => exportOrdersPdf({ orders: sort.sorted, periodsOf: (o) => orderPeriods(o, groupBy), groupBy, range, withDetail: true, typeLabel: TYPE_LABEL[type] })}>
              Lista + detalhe de cada OF por {GROUP_LABELS[groupBy].toLowerCase()}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>


      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[860px] table-fixed text-sm" data-testid="costs-orders-table">
            <colgroup>
              <col /><col className="w-[5.5rem]" /><col className="w-[5.75rem]" /><col className="w-[5.75rem]" /><col className="w-[5.25rem]" /><col className="w-[6rem]" />
              <col className="w-[5.25rem]" /><col className="w-[5.25rem]" /><col className="w-[5.25rem]" /><col className="w-[6.5rem]" />
            </colgroup>
            <thead className="bg-muted/50 text-[11px] uppercase tracking-wide text-muted-foreground">
              <tr>
                <SortHeader k="name" label="OF" sort={sort} firstDir="asc" className="pl-4 pr-2 py-3" testid="costs-sort-name" />
                <SortHeader k="hours" label="Horas OF" sort={sort} align="right" className="px-2 py-3" testid="costs-sort-hours" />
                <SortHeader k="modelagem" label="Modelagem" sort={sort} align="right" className="px-2 py-3" testid="costs-sort-modelagem" />
                <SortHeader k="mesa" label="Mesa corte" sort={sort} align="right" className="px-2 py-3" testid="costs-sort-mesa" />
                <SortHeader k="electricity" label="Eletric." sort={sort} align="right" className="px-2 py-3" testid="costs-sort-electricity" />
                <SortHeader k="depreciation" label="Deprec." sort={sort} align="right" className="px-2 py-3" testid="costs-sort-depreciation" />
                <SortHeader k="punctual" label="Pontuais" sort={sort} align="right" className="px-2 py-3" testid="costs-sort-punctual" />
                <SortHeader k="wear" label="Desgaste" sort={sort} align="right" className="px-2 py-3" testid="costs-sort-wear" />
                <SortHeader k="maintenance" label="Manut." sort={sort} align="right" className="px-2 py-3" testid="costs-sort-maintenance" />
                <SortHeader k="total" label="Custo OF" sort={sort} align="right" className="pl-2 pr-4 py-3" testid="costs-sort-total" />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={10} className="py-12 text-center"><Loader2 className="h-5 w-5 animate-spin inline text-muted-foreground" /></td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={10} className="py-12 text-center text-muted-foreground" data-testid="costs-orders-empty">Sem cortes neste período.</td></tr>
              ) : sort.sorted.map((o) => [
                <tr key={o.key} className={`border-t cursor-pointer hover:bg-muted/30 ${open[o.key] ? "bg-muted/20" : ""}`} onClick={() => setOpen((s) => ({ ...s, [o.key]: !s[o.key] }))} data-testid={`costs-order-row-${o.key}`}>
                  <td className="pl-4 pr-2 py-2.5">
                    <div className="font-medium truncate flex items-center gap-1.5" title={o.sample_description}>
                      <ChevronRight className={`h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform ${open[o.key] ? "rotate-90" : ""}`} />
                      <span className="truncate">{o.name}</span>
                    </div>
                    <div className="text-xs text-muted-foreground truncate">{o.cuts_count} cortes · {o.days_count} dia(s) · {fmtM(o.perimeter)}</div>
                  </td>
                  <td className="px-2 py-2.5 text-right tabular-nums whitespace-nowrap">
                    {fmtH(o.hours)}
                    <div className="text-[11px] text-muted-foreground">{fmtH(o.between_hours)} entre</div>
                  </td>
                  <td className="px-2 py-2.5 text-right tabular-nums whitespace-nowrap">{fmtEur(o.cost_modelagem)}</td>
                  <td className="px-2 py-2.5 text-right tabular-nums whitespace-nowrap">{fmtEur(o.cost_mesa)}</td>
                  <td className="px-2 py-2.5 text-right tabular-nums whitespace-nowrap">{fmtEur(o.cost_electricity)}</td>
                  <td className="px-2 py-2.5 text-right tabular-nums whitespace-nowrap">{fmtEur(o.cost_depreciation)}</td>
                  <td className="px-2 py-2.5 text-right tabular-nums whitespace-nowrap">{fmtEur(o.cost_punctual)}</td>
                  <td className="px-2 py-2.5 text-right tabular-nums whitespace-nowrap">{fmtEur(o.cost_wear)}</td>
                  <td className="px-2 py-2.5 text-right tabular-nums whitespace-nowrap">{fmtEur(o.cost_maintenance)}</td>
                  <td className="pl-2 pr-4 py-2.5 text-right tabular-nums whitespace-nowrap font-semibold" data-testid={`costs-order-total-${o.key}`}>{fmtEur(o.cost_total)}</td>
                </tr>,
                open[o.key] && (
                  <tr key={`${o.key}-open`} className="border-t">
                    <td colSpan={10} className="p-0"><OrderBreakdown o={o} groupBy={groupBy} range={range} /></td>
                  </tr>
                ),
              ])}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// ------------------------------------------------------------------ Dias
const DAY_FIELDS = [
  ["modelagem_people", "Modelagem · pessoas"],
  ["modelagem_rate", "Modelagem · € pessoa/dia"],
  ["mesa_people", "Mesa de corte · pessoas"],
  ["mesa_rate", "Mesa de corte · € pessoa/dia"],
  ["electricity_day", "Eletricidade · €/dia"],
];

function DaysTab({ range, setRange, version, onChanged, groupBy, setGroupBy }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const reqId = useRef(0);
  const load = useCallback(() => {
    const id = ++reqId.current;
    setLoading(true);
    const p = {};
    if (range.start) p.start = range.start;
    if (range.end) p.end = range.end;
    listCostDays(p)
      .then((r) => { if (id === reqId.current) setData(r); })
      .catch(() => { if (id === reqId.current) toast.error("Falha ao carregar dias"); })
      .finally(() => { if (id === reqId.current) setLoading(false); });
  }, [range.start, range.end]);
  useEffect(() => { load(); }, [load, version]);
  const [q, setQ] = useState("");
  const [onlyCustom, setOnlyCustom] = useState(false);
  const rows = useMemo(() => {
    let l = data?.days || [];
    if (onlyCustom) l = l.filter((d) => d.overridden);
    const n = norm(q.trim());
    if (n) {
      l = l.filter((d) => norm(`${fmtDate(d.date)} ${d.date} ${(d.punctual_items || []).map((p) => p.description).join(" ")}`).includes(n));
    }
    return l;
  }, [data, q, onlyCustom]);
  const groups = useMemo(() => aggregateDays(rows, groupBy), [rows, groupBy]);
  const sort = useTableSort(groups, DAY_GETTERS, "date", "asc");
  const [open, setOpen] = useState({});
  const toggleOpen = (k) => setOpen((s) => ({ ...s, [k]: !s[k] }));
  const single = groupBy === "day";
  const sortTh = (k, label, align = "right", firstDir = "desc", cls = "px-2 py-3") => (
    <SortHeader k={k} label={label} sort={sort} align={align} firstDir={firstDir} className={cls} testid={`costs-days-sort-${k}`} />
  );

  return (
    <div className="space-y-4">
      <Card className="p-3 sm:p-4 flex flex-col lg:flex-row lg:items-center gap-3">
        <RangeBar range={range} setRange={setRange} />
        <div className="relative flex-1 min-w-[200px]">
          <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
          <Input placeholder="Procurar dia (ex.: 05/11) ou custo pontual…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="costs-days-search" />
        </div>
        <Button variant={onlyCustom ? "default" : "outline"} size="sm" onClick={() => setOnlyCustom((v) => !v)} data-testid="costs-days-only-custom">
          Só personalizados
        </Button>
      </Card>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">Ver por:</span>
          <GroupByToggle value={groupBy} onChange={(v) => { setGroupBy(v); setOpen({}); }} testid="costs-days-groupby" />
        </div>
        <p className="text-xs text-muted-foreground">Clique numa linha para ver como o custo é dividido pelas OF.{single ? " O lápis altera os valores só desse dia." : " Cada dia é calculado à parte e depois somado."}</p>
        <Button size="sm" variant="outline" disabled={!groups.length} data-testid="costs-days-pdf"
          onClick={() => exportPeriodsPdf({ groups: sort.sorted, groupBy, range, rates: data?.rates })}>
          <FileDown className="h-4 w-4 mr-1" /> Exportar PDF
        </Button>
      </div>
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="costs-days-table">
            <thead className="bg-muted/50 text-[11px] uppercase tracking-wide text-muted-foreground">
              <tr>
                {sortTh("date", GROUP_LABELS[groupBy], "left", "asc", "pl-4 pr-2 py-3")}
                {sortTh("modelagem", "Modelagem")}
                {sortTh("mesa", "Mesa corte")}
                {sortTh("electricity", "Eletric.")}
                {sortTh("depreciation", "Depreciação")}
                {sortTh("punctual", "Pontuais")}
                {sortTh("fixed", single ? "Custo dia" : "Custo fixo")}
                {sortTh("per_hour", "€/hora")}
                {sortTh("of_hours", "Horas OF")}
                {sortTh("cuts", "Cortes")}
                <th className="px-3 py-3" />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={11} className="py-12 text-center"><Loader2 className="h-5 w-5 animate-spin inline text-muted-foreground" /></td></tr>
              ) : !groups.length ? (
                <tr><td colSpan={11} className="py-12 text-center text-muted-foreground" data-testid="costs-days-empty">Sem dias com produção para estes filtros.</td></tr>
              ) : sort.sorted.map((g) => {
                const d = single ? g.days[0] : null;
                return [
                  <tr key={g.key} className={`border-t cursor-pointer hover:bg-muted/30 ${open[g.key] ? "bg-muted/20" : ""}`} onClick={() => toggleOpen(g.key)} data-testid={`costs-day-row-${g.key}`}>
                    <td className="pl-4 pr-2 py-2.5 font-medium whitespace-nowrap">
                      <span className="inline-flex items-center gap-1.5">
                        <ChevronRight className={`h-3.5 w-3.5 text-muted-foreground transition-transform ${open[g.key] ? "rotate-90" : ""}`} />
                        {g.label}
                      </span>
                      {g.sub && <div className="text-[11px] text-muted-foreground font-normal pl-5">{g.sub}</div>}
                      {!single && <div className="text-[11px] text-muted-foreground font-normal pl-5">{g.days.length} dia(s)</div>}
                      {single && d.overridden && <div className="pl-5"><span className="inline-flex px-1.5 py-0.5 rounded border border-sky-200 bg-sky-50 text-sky-700 text-[10px]" data-testid={`costs-day-custom-${d.date}`}>personalizado</span></div>}
                    </td>
                    <td className="px-2 py-2.5 text-right tabular-nums">{fmtEur(g.cost_modelagem)}{single && <div className="text-[11px] text-muted-foreground">{d.effective.modelagem_people} × {fmtEur(d.effective.modelagem_rate)}</div>}</td>
                    <td className="px-2 py-2.5 text-right tabular-nums">{fmtEur(g.cost_mesa)}{single && <div className="text-[11px] text-muted-foreground">{d.effective.mesa_people} × {fmtEur(d.effective.mesa_rate)}</div>}</td>
                    <td className="px-2 py-2.5 text-right tabular-nums">{fmtEur(g.cost_electricity)}</td>
                    <td className="px-2 py-2.5 text-right tabular-nums">{fmtEur(g.cost_depreciation)}</td>
                    <td className="px-2 py-2.5 text-right tabular-nums" title={single ? (d.punctual_items || []).map((p) => `${p.description}: ${fmtEur(p.share)}`).join("\n") : undefined}>{fmtEur(g.cost_punctual)}</td>
                    <td className="px-2 py-2.5 text-right tabular-nums font-semibold">{fmtEur(g.fixed_cost)}</td>
                    <td className="px-2 py-2.5 text-right tabular-nums">{fmtEur(g.cost_per_hour)}<div className="text-[11px] text-muted-foreground whitespace-nowrap">{single ? `÷ ${fmtH(g.of_hours)}` : "média"}</div></td>
                    <td className="px-2 py-2.5 text-right tabular-nums">{fmtH(g.of_hours)}<div className="text-[11px] text-muted-foreground">{g.orders.length} OF</div></td>
                    <td className="px-2 py-2.5 text-right tabular-nums">{g.cuts_count}</td>
                    <td className="px-2 py-2.5 text-right">
                      {single && <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); setEditing(d); }} data-testid={`costs-day-edit-${d.date}`}><Pencil className="h-4 w-4" /></Button>}
                    </td>
                  </tr>,
                  open[g.key] && (
                    <tr key={`${g.key}-open`} className="border-t">
                      <td colSpan={11} className="p-0"><DayBreakdown g={g} groupBy={groupBy} rates={data?.rates} range={range} /></td>
                    </tr>
                  ),
                ];
              })}
            </tbody>
          </table>
        </div>
      </Card>
      <DayDialog day={editing} defaults={data?.settings} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); onChanged(); }} />
    </div>
  );
}

function DayDialog({ day, defaults, onClose, onSaved }) {
  const [vals, setVals] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!day) return;
    const v = {};
    DAY_FIELDS.forEach(([k]) => { const o = day.override?.[k]; v[k] = o === null || o === undefined ? "" : String(o); });
    setVals(v);
  }, [day]);

  async function save() {
    const payload = {};
    for (const [k, l] of DAY_FIELDS) {
      if (vals[k] === "" || vals[k] === undefined) continue;
      const n = num(vals[k]);
      if (n === null || n < 0) { toast.error(`Valor inválido: ${l}`); return; }
      payload[k] = n;
    }
    setSaving(true);
    try {
      await saveCostDay(day.date, payload);
      toast.success(`Custos de ${fmtDate(day.date)} guardados`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || "Falha ao guardar");
    } finally { setSaving(false); }
  }
  async function reset() {
    setSaving(true);
    try { await resetCostDay(day.date); toast.success("Dia reposto para os valores padrão"); onSaved(); }
    catch { toast.error("Falha ao repor"); } finally { setSaving(false); }
  }

  return (
    <Dialog open={!!day} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="costs-day-dialog">
        <DialogHeader>
          <DialogTitle>Custos de {day ? fmtDate(day.date) : ""}</DialogTitle>
          <DialogDescription>Deixe em branco para usar o valor padrão (indicado no campo).</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {DAY_FIELDS.map(([k, l]) => (
            <div key={k} className="space-y-1.5">
              <Label htmlFor={`day-${k}`} className="text-xs">{l}</Label>
              <Input id={`day-${k}`} inputMode="decimal" data-testid={`costs-day-input-${k}`} placeholder={defaults ? `padrão: ${defaults[k]}` : ""}
                value={vals[k] ?? ""} onChange={(e) => setVals((s) => ({ ...s, [k]: e.target.value }))} />
            </div>
          ))}
        </div>
        <DialogFooter className="gap-2">
          {day?.overridden && (
            <Button variant="outline" onClick={reset} disabled={saving} data-testid="costs-day-reset"><RotateCcw className="h-4 w-4 mr-1" /> Repor padrão</Button>
          )}
          <Button onClick={save} disabled={saving} data-testid="costs-day-save">{saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Guardar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ------------------------------------------------------------------ Custos pontuais
const MONTHS = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];
function periodLabel(p) {
  if (p.scope === "year") return `Ano ${p.period}`;
  const [y, m] = p.period.split("-");
  return `${MONTHS[Number(m) - 1]} ${y}`;
}

function PunctualTab({ onChanged }) {
  const now = new Date();
  const blank = { description: "", amount: "", scope: "month", year: String(now.getFullYear()), month: String(now.getMonth() + 1).padStart(2, "0") };
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(blank);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    listPunctualCosts().then(setItems).catch(() => toast.error("Falha ao carregar custos pontuais")).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  const [pq, setPq] = useState("");
  const prows = useMemo(() => {
    const n = norm(pq.trim());
    if (!n) return items;
    return items.filter((p) => norm(`${p.description} ${periodLabel(p)} ${p.period}`).includes(n));
  }, [items, pq]);
  const psort = useTableSort(prows, PUNCTUAL_GETTERS, "period", "desc");

  async function submit() {
    const amount = num(form.amount);
    if (!form.description.trim()) return toast.error("Indique a descrição");
    if (amount === null || amount < 0) return toast.error("Valor inválido");
    if (!/^\d{4}$/.test(form.year)) return toast.error("Ano inválido");
    const payload = { description: form.description, amount, scope: form.scope, period: form.scope === "month" ? `${form.year}-${form.month}` : form.year };
    setSaving(true);
    try {
      if (editId) await updatePunctualCost(editId, payload); else await createPunctualCost(payload);
      toast.success(editId ? "Custo pontual atualizado" : "Custo pontual adicionado");
      setForm(blank); setEditId(null); load(); onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao guardar");
    } finally { setSaving(false); }
  }
  function edit(p) {
    const [y, m] = p.period.split("-");
    setEditId(p.id);
    setForm({ description: p.description, amount: String(p.amount), scope: p.scope, year: y, month: m || "01" });
  }
  async function remove(p) {
    if (!window.confirm(`Apagar o custo pontual "${p.description}"?`)) return;
    try { await deletePunctualCost(p.id); toast.success("Custo pontual apagado"); load(); onChanged(); }
    catch { toast.error("Falha ao apagar"); }
  }

  return (
    <div className="grid lg:grid-cols-[380px_1fr] gap-4 items-start">
      <Card className="p-4 space-y-3" data-testid="costs-punctual-form">
        <h3 className="font-semibold">{editId ? "Editar custo pontual" : "Novo custo pontual"}</h3>
        <div className="space-y-1.5">
          <Label htmlFor="p-desc">Descrição</Label>
          <Input id="p-desc" data-testid="costs-punctual-description" placeholder="ex.: Seguro, afiação, software…" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="p-amount">Valor (€)</Label>
          <Input id="p-amount" inputMode="decimal" data-testid="costs-punctual-amount" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} />
        </div>
        <div className="space-y-1.5">
          <Label>Aplicar a</Label>
          <Select value={form.scope} onValueChange={(v) => setForm((f) => ({ ...f, scope: v }))}>
            <SelectTrigger data-testid="costs-punctual-scope"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="month">Um mês</SelectItem><SelectItem value="year">Um ano</SelectItem></SelectContent>
          </Select>
        </div>
        <div className="flex gap-2">
          {form.scope === "month" && (
            <Select value={form.month} onValueChange={(v) => setForm((f) => ({ ...f, month: v }))}>
              <SelectTrigger className="flex-1" data-testid="costs-punctual-month"><SelectValue /></SelectTrigger>
              <SelectContent>{MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1).padStart(2, "0")}>{m}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <Input className="w-[110px]" inputMode="numeric" data-testid="costs-punctual-year" value={form.year} onChange={(e) => setForm((f) => ({ ...f, year: e.target.value }))} />
        </div>
        <p className="text-xs text-muted-foreground">O valor é dividido em partes iguais pelos dias com produção desse {form.scope === "month" ? "mês" : "ano"}.</p>
        <div className="flex justify-end gap-2">
          {editId && <Button variant="outline" onClick={() => { setEditId(null); setForm(blank); }}>Cancelar</Button>}
          <Button onClick={submit} disabled={saving} data-testid="costs-punctual-save">
            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />} {editId ? "Guardar" : "Adicionar"}
          </Button>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="p-3 border-b">
          <div className="relative">
            <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
            <Input placeholder="Procurar descrição, mês ou ano…" value={pq} onChange={(e) => setPq(e.target.value)} className="pl-9" data-testid="costs-punctual-search" />
          </div>
        </div>
        <table className="w-full text-sm" data-testid="costs-punctual-table">
          <thead className="bg-muted/50 text-[11px] uppercase tracking-wide text-muted-foreground">
            <tr>
              <SortHeader k="description" label="Descrição" sort={psort} firstDir="asc" className="pl-4 pr-2 py-3" testid="costs-punctual-sort-description" />
              <SortHeader k="period" label="Período" sort={psort} className="px-2 py-3" testid="costs-punctual-sort-period" />
              <SortHeader k="amount" label="Valor" sort={psort} align="right" className="px-2 py-3" testid="costs-punctual-sort-amount" />
              <SortHeader k="per_day" label="Por dia" sort={psort} align="right" className="px-2 py-3" testid="costs-punctual-sort-per_day" />
              <th className="px-3 py-3" />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="py-10 text-center"><Loader2 className="h-5 w-5 animate-spin inline text-muted-foreground" /></td></tr>
            ) : !prows.length ? (
              <tr><td colSpan={5} className="py-10 text-center text-muted-foreground" data-testid="costs-punctual-empty">Sem custos pontuais.</td></tr>
            ) : psort.sorted.map((p) => (
              <tr key={p.id} className="border-t" data-testid={`costs-punctual-row-${p.id}`}>
                <td className="px-4 py-2.5 font-medium">{p.description}</td>
                <td className="px-3 py-2.5">{periodLabel(p)}</td>
                <td className="px-2 py-2.5 text-right tabular-nums">{fmtEur(p.amount)}</td>
                <td className="px-2 py-2.5 text-right tabular-nums">
                  {p.per_day !== null && p.per_day !== undefined ? fmtEur(p.per_day) : "—"}
                  <div className="text-[11px] text-muted-foreground">{p.production_days} dia(s) com produção</div>
                </td>
                <td className="px-3 py-2.5 text-right whitespace-nowrap">
                  <Button variant="ghost" size="icon" onClick={() => edit(p)} data-testid={`costs-punctual-edit-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => remove(p)} data-testid={`costs-punctual-delete-${p.id}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

// ------------------------------------------------------------------ Configuração
const SETTINGS_GROUPS = [
  ["Modelagem", [["modelagem_people", "Nº de pessoas"], ["modelagem_rate", "Valor pessoa/dia (€)"]]],
  ["Mesa de corte", [["mesa_people", "Nº de pessoas"], ["mesa_rate", "Valor pessoa/dia (€)"]]],
  ["Eletricidade", [["electricity_day", "Eletricidade (€/dia)"]]],
  ["Depreciação da máquina", [["depreciation_year", "Valor por ano (€)"], ["depreciation_days_month", "Dias de produção por mês"]]],
  ["Desgaste da máquina", [["wear_cost", "Custo (€)"], ["wear_interval_h", "A cada (horas de corte)"]]],
  ["Manutenção", [["maintenance_cost", "Custo (€)"], ["maintenance_interval_h", "A cada (horas de corte)"]]],
];

function SettingsTab({ onChanged }) {
  const [vals, setVals] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    getCostSettings().then((s) => setVals(Object.fromEntries(Object.entries(s).map(([k, v]) => [k, String(v)])))).catch(() => toast.error("Falha ao carregar configuração"));
  }, []);

  async function save() {
    const payload = {};
    for (const [, fields] of SETTINGS_GROUPS) {
      for (const [k, l] of fields) {
        const n = num(vals[k]);
        if (n === null || n < 0) return toast.error(`Valor inválido: ${l}`);
        if ((k.endsWith("interval_h") || k === "depreciation_days_month") && n <= 0) return toast.error(`${l} tem de ser maior que 0`);
        payload[k] = n;
      }
    }
    setSaving(true);
    try { await saveCostSettings(payload); toast.success("Configuração de custos guardada"); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail?.[0]?.msg || "Falha ao guardar"); }
    finally { setSaving(false); }
  }

  if (!vals) return <div className="py-12 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;
  const per = (c, i) => { const a = num(vals[c]); const b = num(vals[i]); return a !== null && b ? fmtEur(a / b) : "—"; };
  const depYear = num(vals.depreciation_year) || 0;
  const depDays = num(vals.depreciation_days_month) || 22;
  const depDay = depYear / 12 / depDays;
  const rate = (c, i) => { const a = num(vals[c]) || 0; const b = num(vals[i]) || 0; return b > 0 ? a / b : 0; };
  const varPerHour = rate("wear_cost", "wear_interval_h") + rate("maintenance_cost", "maintenance_interval_h");
  const day = (num(vals.modelagem_people) || 0) * (num(vals.modelagem_rate) || 0) + (num(vals.mesa_people) || 0) * (num(vals.mesa_rate) || 0) + (num(vals.electricity_day) || 0) + depDay;

  return (
    <Card className="p-4 sm:p-6 space-y-5" data-testid="costs-settings-form">
      <div>
        <h3 className="font-semibold">Valores padrão</h3>
        <p className="text-sm text-muted-foreground">Aplicam-se a todos os dias com produção, exceto os que alterar no separador “Dias”.</p>
      </div>
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {SETTINGS_GROUPS.map(([title, fields]) => (
          <div key={title} className="rounded-lg border p-4 space-y-3">
            <div className="text-sm font-semibold">{title}</div>
            {fields.map(([k, l]) => (
              <div key={k} className="space-y-1">
                <Label htmlFor={`s-${k}`} className="text-xs">{l}</Label>
                <Input id={`s-${k}`} inputMode="decimal" data-testid={`costs-setting-${k}`} value={vals[k]} onChange={(e) => setVals((s) => ({ ...s, [k]: e.target.value }))} />
              </div>
            ))}
            {title === "Desgaste da máquina" && <div className="text-xs text-muted-foreground">= {per("wear_cost", "wear_interval_h")} por hora de corte</div>}
            {title === "Manutenção" && <div className="text-xs text-muted-foreground">= {per("maintenance_cost", "maintenance_interval_h")} por hora de corte</div>}
            {title === "Depreciação da máquina" && (
              <div className="text-xs text-muted-foreground" data-testid="costs-depreciation-preview">
                = {fmtEur(depYear / 12)} por mês · <span className="font-medium text-foreground">{fmtEur(depDay)} por dia</span>
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-4">
        <div className="text-sm text-muted-foreground" data-testid="costs-settings-preview">
          Custo fixo por dia (sem custos pontuais): <span className="font-semibold text-foreground">{fmtEur(day)}</span>
          {" "}· dividido pelas OF conforme as horas reais de cada dia
          <div className="mt-1" data-testid="costs-settings-variable-preview">
            Desgaste + manutenção: <span className="font-semibold text-foreground">{fmtEur(varPerHour)} por hora de corte real</span>
            {" "}· não entra no custo fixo do dia; é somado a cada OF pelas suas horas de corte
          </div>
        </div>
        <Button onClick={save} disabled={saving} data-testid="costs-settings-save">{saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Guardar</Button>
      </div>
    </Card>
  );
}
